<template>
  <v-dialog
    v-model="showModal"
    content-class="checkin-dialog"
    persistent
    fullscreen
  >
    <div class="content-section">
      <overlay-alert
        :show-progress="showProgress"
        :process-alert="processAlert"
      />
      <h1 class="header-style">
        Check-in
      </h1>
      <p class="subheader-style">
        Fill the fields to checkin.
      </p>
      <div
        v-if="isWorkerDocVerifyFailed"
        class="fail-message-style mb-4"
      >
        <p
          v-for="(item, i) in workerDocVerificationStatus.expired_documents"
          :key="i"
          class="fail-style"
        >
          <v-icon
            color="#C62828"
            size="30"
          >
            mdi-alert
          </v-icon> <br>
          '{{ item.document_type.name }}' is expired, please upload the renewed
          file.
        </p>
      </div>
      <div
        v-if="verificationFailed"
        class="fail-message-style mb-4"
      >
        <p class="fail-style">
          <v-icon
            color="#C62828"
            size="30"
          >
            mdi-alert
          </v-icon> <br>
          {{ verificationFailedMessage }}
        </p>
      </div>
      <v-form
        ref="form"
        v-model="valid"
        class="mt-6"
        lazy-validation
      >
        <div v-if="checkInTypesVisibility">
          <p class="p-0 m-0">
            Select Check-in Type
          </p>
          <v-row dense>
            <v-col
              v-for="type in capitalizedCheckInTypes"
              :key="type.id"
              cols="6"
              class="d-flex justify-center p-0"
            >
              <v-chip
                label
                color="#FFF"
                class="square-chip"
                :class="{ 'selected-chip': selectedCheckinType === type.id }"
                :disabled="
                  checkInType === 'previous' && selectedCheckinType !== type.id
                "
                @click="selectedCheckinType = type.id"
              >
                <div class="d-flex justify-center align-center">
                  <v-icon
                    v-if="type.id === 'staff'"
                    class="pr-2"
                    size="25"
                    color="#66BB6A"
                  >
                    mdi-hard-hat
                  </v-icon>
                  <v-icon
                    v-if="type.id === 'contractor'"
                    size="25"
                    color="#66BB6A"
                    class="pr-2"
                  >
                    mdi-account-hard-hat
                  </v-icon>
                  <v-icon
                    v-if="type.id === 'worker'"
                    size="25"
                    color="#66BB6A"
                    class="pr-2"
                  >
                    mdi-badge-account
                  </v-icon>
                  <v-icon
                    v-if="type.id === 'visitor'"
                    size="25"
                    color="#66BB6A"
                    class="pr-2"
                  >
                    mdi-account
                  </v-icon>
                  <span>{{ type.name }}</span>
                </div>
              </v-chip>
            </v-col>
          </v-row>
        </div>
        <v-row dense>
          <v-col
            :cols="formattedProjectCol"
            class="pt-4"
          >
            <v-combobox
              v-model="selectedProject"
              :items="organizationProjects"
              item-text="name"
              item-value="id"
              return-object
              label="Projects"
              placeholder="Search project by name"
              light
              outlined
              dense
              :menu-props="{ offsetY: true }"
            >
              <template #item="{ item, on, attrs }">
                <v-list-item
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-list-item-content>
                    <v-list-item-title class="project-name">
                      {{ item.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle
                      v-if="
                        item.gate !== null && item.gate.location_name !== null
                      "
                      class="project-location"
                    >
                      <v-icon
                        size="16"
                        class="location-icon"
                      >
                        mdi-map-marker
                      </v-icon>
                      <span class="location-label">
                        {{ item.gate.location_name }}
                      </span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>

              <template #selection="{ item }">
                <div class="selected-project-name">
                  {{ item.name }}
                </div>
              </template>
            </v-combobox>
          </v-col>
          <v-col
            v-if="selectedProject !== null && gatesList.length !== 1"
            cols="6"
            class="pt-4"
          >
            <v-select
              v-model="selectedGate"
              light
              color="black"
              :disabled="checkInType === 'previous'"
              flat
              dense
              :items="gatesList"
              :rules="[(v) => !!v || 'This field is required']"
              item-text="name"
              item-value="id"
              label="Gates"
              outlined
              background-color="#FFF"
              class="small-select checkin"
            />
          </v-col>
        </v-row>
        <v-row
          v-if="
            selectedProject !== null &&
              selectedProject.gate !== null &&
              selectedProject.gate.location_name
          "
        >
          <v-col>
            <div class="location-wrapper">
              <div class="location-label-row">
                <v-icon
                  size="16"
                  class="location-icon"
                >
                  mdi-map-marker
                </v-icon>
                <span class="location-label"> Location </span>
              </div>

              <div class="location-value">
                {{ selectedGateLocation }}
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <!-- <v-col
            cols="12"
            class="pt-4"
            v-if="
              selectedCheckinType === 'worker'&& selectedProject !== null && projectBudgets.length === 0
            "
          >
            <p>No items for checkin</p>
          </v-col> -->
          <v-col
            v-if="
              selectedCheckinType === 'worker' &&
                selectedProject !== null &&
                isBudgetCheckin &&
                !isIssueCheckin
            "
            cols="12"
            class="pt-0"
          >
            <v-autocomplete
              v-model="selectedBudgetItem"
              :items="projectBudgets"
              item-value="id"
              color="#37474F"
              outlined
              background-color="#FFF"
              dense
              light
              item-text="name"
              :rules="[(v) => !!v || 'This field is required']"
              label="Select Budget"
              hint="Select your work item from the available list for continuing the check-in process."
              persistent-hint
            />
          </v-col>
          <v-col
            v-if="
              selectedCheckinType === 'worker' &&
                selectedProject !== null &&
                isBudgetCheckin &&
                isIssueCheckin
            "
            cols="12"
            class="pt-0"
          >
            <p class="p-0 m-0">
              Select Work Type
            </p>
            <div
              v-if="projectIssues.length === 0 && projectBudgets.length === 0"
            >
              <p class="p-0 m-0">
                <small><v-icon color="black">mdi-exclamation</v-icon> No budget or
                  issue found</small>
              </p>
            </div>
            <div v-else>
              <v-radio-group
                v-model="workType"
                row
                light
                :rules="[(v) => !!v || 'This field is required']"
                @change="isWorkTypeChanged"
              >
                <v-radio
                  v-if="projectBudgets.length > 0"
                  label="Construction Work"
                  value="project-work"
                  light
                  color="#37474F"
                >
                  <template #label>
                    <p class="type-bottom">
                      Budget
                    </p>
                  </template>
                </v-radio>
                <v-radio
                  v-if="projectIssues.length > 0"
                  label="Issue Fix"
                  value="project-issue"
                  light
                  color="#37474F"
                >
                  <template #label>
                    <p class="type-bottom">
                      Issue
                    </p>
                  </template>
                </v-radio>
              </v-radio-group>
              <v-container fluid>
                <div v-if="isBudgetTypeSelected">
                  <v-autocomplete
                    v-model="selectedBudgetItem"
                    :items="projectBudgets"
                    item-value="id"
                    color="#37474F"
                    outlined
                    background-color="#FFF"
                    dense
                    light
                    item-text="name"
                    :rules="[(v) => !!v || 'This field is required']"
                    label="Select Work Item"
                    prepend-icon="mdi-account-hard-hat"
                    hint="Select your work item from the available list for continuing the check-in process."
                    persistent-hint
                  />
                </div>
                <div v-if="isIssueTypeSelected">
                  <v-autocomplete
                    v-model="selectedProjectIssue"
                    :items="projectIssues"
                    item-value="id"
                    color="#37474F"
                    outlined
                    background-color="#FFF"
                    dense
                    light
                    item-text="title"
                    :rules="[(v) => !!v || 'This field is required']"
                    label="Select Issue"
                    prepend-icon="mdi-tools"
                    hint="Select a project issue from this available list for continuing the check-in process."
                    persistent-hint
                  />
                </div>
              </v-container>
            </div>
          </v-col>
          <v-col
            v-if="
              selectedCheckinType === 'worker' &&
                !isBudgetCheckin &&
                isIssueCheckin
            "
            cols="12"
          >
            <v-autocomplete
              v-model="selectedProjectIssue"
              :items="projectIssues"
              item-value="id"
              color="#37474F"
              outlined
              background-color="#FFF"
              dense
              light
              item-text="title"
              :rules="[(v) => !!v || 'This field is required']"
              label="Select Issue"
              prepend-icon="mdi-tools"
              hint="Select a project issue from this available list for continuing the check-in process."
              persistent-hint
            />
          </v-col>
          <v-col
            v-if="selectedCheckinType === 'visitor'"
            cols="12"
          >
            <v-textarea
              v-model="visitPurpose"
              dense
              :rules="[(v) => !!v || 'This field is required']"
              color="#000000"
              light
              outlined
              label="Visit Purpose*"
              background-color="#FFF"
            />
          </v-col>
          <v-col
            v-if="selectedCheckinType === 'contractor'"
            cols="12"
          >
            <v-autocomplete
              v-model="selectedContract"
              :items="projectContracts"
              item-value="id"
              color="#37474F"
              outlined
              background-color="#FFF"
              dense
              light
              item-text="name"
              :rules="[(v) => !!v || 'This field is required']"
              label="Select a Contract*"
              prepend-inner-icon="mdi-file-document-multiple"
              persistent-hint
            />
          </v-col>
          <v-col
            v-if="questions.length > 0 && questionaireEnableCheck === true"
            cols="12"
          >
            <p>Questionaire</p>
            <div
              v-for="(item, i) in questions"
              :key="item.id"
              style="
                border: 0.5px solid #ccc;
                margin: 10px;
                padding: 10px;
                border-radius: 10px;
              "
            >
              <p class="question-style">
                {{ i + 1 }}. {{ item.question }}
              </p>
              <div
                v-for="(answer, ansindex) in item.answers"
                :key="`answer - ${ansindex}`"
                class="answer-div"
              >
                <v-checkbox
                  v-if="answer.answer !== null"
                  v-model="answerSelected[item.id]"
                  :value="answer.answer"
                  hide-details
                  dense
                  filled
                  color="#FF3700"
                  light
                  outlined
                >
                  <template #label>
                    <span class="input__label">{{ answer.answer }}</span>
                  </template>
                </v-checkbox>
              </div>
              <br>
            </div>
          </v-col>
        </v-row>
      </v-form>
      <v-btn
        fab
        color="#FFF"
        small
        rounded
        elevation="0"
        class="close-button"
        @click="showModal = false"
      >
        <v-icon color="#37474f">
          mdi mdi-close
        </v-icon>
      </v-btn>
      <v-btn
        color="#64CA7F"
        class="btn-style"
        elevation="0"
        @click="checkIn()"
      >
        Check-in
      </v-btn>
      <center-spinner :loading="loading" />
    </div>
  </v-dialog>
</template>

<script>
import spinner from '../../components/SpinnerCentre.vue';
import OverlayAlert from 'src/views/components/OverlayAlert';
import moment from 'moment';
import Constants from 'src/constants';
export default {
  name: 'CheckInSheet',
  components: {
    'center-spinner': spinner,
    'overlay-alert': OverlayAlert,
  },
  filters: {
    arrangePretty (val) {
      if (val === 'staff') {
        return 'Site Manager';
      } else {
        return val;
      }
    },
  },
  props: {
    previousCheckin: {
      type: Object,
      required: false,
    },
    locationEnableCheck: {
      type: Boolean,
    },
    checkInType: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    scannedCode: '',
    showModal: true,
    selectedGate: null,
    selectedProject: null,
    selectedCheckinType: null,
    isBudgetTypeSelected: true,
    selectedProjectIssue: '',
    isIssueTypeSelected: false,
    showInstancesForm: false,
    locationFetchFailed: false,
    visitPurpose: null,
    selectedContract: null,
    answerSelected: {},
    workType: 'project-work',
    verificationFailed: false,
    verificationFailedMessage: '',
    showProgress: false,
    processAlert: '',
    selectedBudgetItem: null,
    valid: false,
    longitudeValue: null,
    latitudeValue: null,
    selectedGateLocation: null,
  }),
  computed: {
    selectedOrganization () {
      return this.$store.getters.getSelectedOrganization;
    },
    isStoredOrganizationAvailable () {
      if (Object.keys(this.selectedOrganization).length > 0) {
        return true;
      }
      return false;
    },
    documentVerificationCheck () {
      return this.orgSettings.document_verification;
    },
    workerDocVerificationStatus () {
      return this.$store.getters['swiftuser/getWorkerDocVerifyStatus'];
    },
    capitalizedCheckInTypes () {
      return this.checkInTypes.map((type) => ({
        id: type,
        name:
          type === 'staff'
            ? 'Site Manager'
            : type.charAt(0).toUpperCase() + type.slice(1),
      }));
    },
    questionaireEnableCheck () {
      return this.orgSettings.questionnaire_verification;
    },
    locationCheck () {
      return this.orgSettings.location_verification;
    },
    gatesList () {
      return this.$store.getters['projects/getOrganizationProjectGates'];
    },
    organizationProjects () {
      return this.$store.getters['projects/getOrganizationProjects'];
    },
    formattedProjectCol () {
      if (this.selectedProject === null) {
        return 12;
      } else {
        return this.gatesList.length === 1 ? 12 : 6;
      }
    },
    checkInTypes () {
      if (this.isStoredOrganizationAvailable === true) {
        return this.selectedOrganization.types;
      }
      return [];
    },
    orgSettings () {
      return this.$store.getters['organization/getOrganizationSettings'];
    },
    isBudgetCheckin () {
      return this.orgSettings.budget_checkin;
    },
    isIssueCheckin () {
      return this.orgSettings.issue_checkin;
    },
    checkInId () {
      return this.$store.getters['swiftuser/getCheckInId'];
    },
    isQuestionaire () {
      return this.orgSettings.questionnaire_verification;
    },
    filteredProjects () {
      if (!this.selectedProject || typeof this.selectedProject !== 'string') {
        return [];
      }
      const query = this.selectedProject.toLowerCase();
      return this.organizationProjects.filter((project) =>
        project.name.toLowerCase().includes(query),
      );
    },
    questions () {
      return this.$store.getters['questions/getOrgQuestions'];
    },
    projectContracts () {
      return this.$store.getters['projects/getProjectContracts'];
    },
    projectIssues () {
      return this.$store.getters['projects/getProjectIssues'];
    },
    projectBudgets () {
      return this.$store.getters['projects/getProjectBudgets'];
    },
    isWorkerDocVerifyFailed () {
      if (
        Object.keys(this.workerDocVerificationStatus).length > 0 &&
        this.workerDocVerificationStatus.verification_status === 'failed'
      ) {
        return true;
      }
      return false;
    },
    checkInTypesVisibility () {
      if (this.capitalizedCheckInTypes.length > 1) {
        return true;
      }
      return false;
    },
    gatesVisibility () {
      if (this.gatesList.length > 1 || this.gatesList.length === 0) {
        return true;
      }
      return false;
    },
  },
  watch: {
    showModal: function (newval, oldval) {
      if (newval === false) {
        this.$emit('closed');
      }
    },
    async selectedProject (newVal) {
      this.selectedGate = null;
      if (newVal !== null) {
        await this.getOrganizationProjectGates().then(() => {
          if (this.previousCheckin !== undefined) {
            this.selectedGate = this.previousCheckin.checkin_gate.id;
          }
        });
      }
      if (this.selectedCheckinType === 'contractor') {
        this.fetchProjectContracts();
      }
      if (this.selectedCheckinType === 'worker') {
        this.fetchProjectBudgets();
        this.fetchProjectIssues();
      }
    },
    selectedContract (newVal) {
      if (newVal !== null) {
        this.verificationFailed = false;
        this.verificationFailedMessage = '';
      }
    },
    selectedBudgetItem (newVal) {
      if (newVal !== null) {
        this.verificationFailed = false;
        this.verificationFailedMessage = '';
        this.documentVerificationCheck === true && this.documentVerification();
      }
    },
    selectedProjectIssue (newVal) {
      if (newVal !== null) {
        this.verificationFailed = false;
        this.verificationFailedMessage = '';
        this.documentVerificationCheck === true && this.documentVerification();
      }
    },
    selectedCheckinType (newVal) {
      if (newVal !== null) {
        this.questionaireEnableCheck === true && this.fetchOrgQuestions();
        this.isBudgetTypeSelected = false;
        this.selectedProjectIssue = '';
        this.isIssueTypeSelected = false;
        this.showInstancesForm = false;
        this.locationFetchFailed = false;
        this.visitPurpose = null;
        this.selectedContract = null;
        this.answerSelected = {};
        this.workType = null;
        this.verificationFailed = false;
        this.verificationFailedMessage = '';
        this.showProgress = false;
        this.processAlert = '';
        this.selectedBudgetItem = null;
        this.$store.dispatch('swiftuser/clearWorkerDocVerifyStatus');
        this.$store.dispatch('questions/clearStoredQuestions');
        if (this.selectedCheckinType === 'worker') {
          if (this.selectedProject !== null) {
            this.fetchProjectBudgets();
            this.fetchProjectIssues();
          }
        }
        if (this.selectedCheckinType === 'contractor') {
          if (this.selectedProject !== null) {
            this.fetchProjectContracts();
          }
        }
      }
    },
    selectedGate (newVal) {
      let gate = null;

      if (newVal !== null) {
        gate = this.gatesList.find(function (g) {
          return g.id === newVal || (newVal && g.id === newVal.id);
        });
      }

      if (gate) {
        // Priority order for display
        if (gate.location_name) {
          this.selectedGateLocation = gate.location_name;
        } else if (gate.address && gate.address.line1) {
          this.selectedGateLocation = gate.address.line1;
        } else if (gate.name) {
          this.selectedGateLocation = gate.name;
        } else {
          this.selectedGateLocation = '—';
        }
      } else {
        this.selectedGateLocation = '—';
      }

      // reset errors
      if (newVal !== null) {
        this.verificationFailed = false;
        this.verificationFailedMessage = '';
      }
    },

    isBudgetTypeSelected (newVal) {
      if (newVal === true) {
        this.fetchOrgQuestions();
      }
    },
  },
  async mounted () {
    await this.fetchLocationDetails();
    this.$store.dispatch('swiftuser/clearWorkerDocVerifyStatus');
    this.$store.dispatch('questions/clearStoredQuestions');
    if (this.previousCheckin !== undefined && this.previousCheckin !== null) {
      this.selectedCheckinType = this.previousCheckin.type;
    }
    if (this.checkInTypes.length === 1) {
      this.selectedCheckinType = this.checkInTypes[0];
    }
    await this.getOrganizationProjects();
    await this.fetchOrgDetails();
    if (this.previousCheckin !== undefined && this.previousCheckin !== null) {
      this.selectedProject = this.previousCheckin.project;
    }
  },
  methods: {
    isWorkTypeChanged () {
      this.$store.dispatch('swiftuser/clearSavedWorkDetails');
      if (this.workType === 'project-work') {
        this.isIssueTypeSelected = false;
        this.selectedProjectIssue = '';
        this.isBudgetTypeSelected = true;
      } else {
        this.isBudgetTypeSelected = false;
        this.selectedBudgetItem = '';
        this.isIssueTypeSelected = true;
      }
    },
    async fetchProjectBudgets () {
      this.loading = true;
      await this.$store
        .dispatch('projects/fetchProjectBudgets', {
          projectId: this.selectedProject.id,
        })
        .then(() => {
          this.selectedBudgetItem =
            this.projectBudgets.length > 0 ? this.projectBudgets[0].id : null;
        });
      this.loading = false;
    },
    async fetchProjectIssues () {
      this.loading = true;
      await this.$store.dispatch('projects/fetchProjectIssues', {
        paramData: {
          params: {
            status: ['pending', 'active'],
          },
        },
        projectId: this.selectedProject.id,
      });
      this.loading = false;
    },
    async fetchOrgDetails () {
      this.loading = true;
      await this.$store
        .dispatch(
          'organization/fetchOrganizationSettings',
          this.selectedOrganization,
        )
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    resetPage () {
      this.selectedGate = null;
      this.selectedProject = null;
      this.selectedCheckinType = null;
      this.isBudgetTypeSelected = false;
      this.selectedProjectIssue = '';
      this.isIssueTypeSelected = false;
      this.showInstancesForm = false;
      this.locationFetchFailed = false;
      this.visitPurpose = null;
      this.selectedContract = null;
      this.answerSelected = {};
      this.workType = null;
      this.verificationFailed = false;
      this.verificationFailedMessage = '';
      this.showProgress = false;
      this.processAlert = '';
      this.selectedBudgetItem = null;
      this.$store.dispatch('swiftuser/clearWorkerDocVerifyStatus');
    },
    async fetchOrgQuestions () {
      await this.$store.dispatch('questions/fetchOrgQuestions', {
        userType: this.selectedCheckinType,
        organizationExtId: this.selectedOrganization.organization.external_id,
      });
    },
    async getOrganizationProjectGates () {
      this.loading = true;
      const projectId = this.selectedProject.id;
      await this.$store
        .dispatch('projects/fetchOrganizationProjectGates', {
          projectId: projectId,
        })
        .then(() => {
          if (this.gatesList.length === 1) {
            this.selectedGate = this.gatesList[0].id;
            this.selectedGateLocation = this.gatesList[0].location_name;
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
      this.loading = false;
    },
    async getOrganizationProjects () {
      this.loading = true;
      await this.$store
        .dispatch('projects/fetchProjects', {
          organizationExtId: this.selectedOrganization.organization.external_id,
          params: {
            excludeStatuses: ['completed'],
          },
        })
        .then(() => {
          if (this.organizationProjects.length === 1) {
            this.selectedProject = this.organizationProjects[0];
            this.loading = false;
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    async fetchLocationDetails () {
      if (!this.locationEnableCheck) {
        return;
      }
      if (navigator.geolocation) {
        this.showProgress = true;
        this.processAlert = 'Checking In...';
        navigator.geolocation.getCurrentPosition(
          this.showPosition.bind(this),
          this.showError.bind(this),
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          },
        );
      } else {
        this.showProgress = false;
        this.verificationFailed = true;
        this.verificationFailedMessage =
          'Geolocation is not supported by this browser.';
      }
    },
    showPosition (position) {
      this.latitudeValue = position.coords.latitude;
      this.longitudeValue = position.coords.longitude;
      this.showProgress = false;
    },
    showError (error) {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          this.showProgress = false;
          this.locationFetchFailed = true;
          this.verificationFailed = true;
          this.verificationFailedMessage =
            'User denied the request for location access. Please close the tab and relaunch our app once access is enabled.';
          break;
        case error.POSITION_UNAVAILABLE:
          this.showProgress = false;
          this.locationFetchFailed = true;
          this.verificationFailed = true;
          this.verificationFailedMessage =
            'Location information is unavailable.';
          break;
        case error.TIMEOUT:
          this.showProgress = false;
          this.locationFetchFailed = true;
          this.verificationFailed = true;
          this.verificationFailedMessage =
            'The request to get user location timed out.';
          break;
        case error.UNKNOWN_ERROR:
          this.showProgress = false;
          this.locationFetchFailed = true;
          this.verificationFailed = true;
          this.verificationFailedMessage = 'An unknown error occurred.';
          break;
      }
    },
    async verifyLocation () {
      if (this.locationCheck === false) {
        return;
      }
      this.$store.dispatch('swiftuser/clearScannedLocationName');
      this.loading = true;
      await this.$store
        .dispatch('swiftuser/verifyLocation', {
          orgExtId: this.selectedOrganization.organization.external_id,
          details: {
            project_gate_id:
              typeof this.selectedGate === 'object'
                ? this.selectedGate.id
                : this.selectedGate,
            checkin_latitude:
              this.latitudeValue != null ? this.latitudeValue.toString() : null,
            checkin_longitude:
              this.longitudeValue != null
                ? this.longitudeValue.toString()
                : null,
            checkin_type: 'check-in',
            location_access: !!(
              this.longitudeValue != null && this.latitudeValue != null
            ),
          },
        })
        .then((response) => {
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.locationFetchFailed = true;
          this.verificationFailed = true;
          this.verificationFailedMessage = error.response.data.message;
          setTimeout(() => {
            this.showModal = false;
          }, 2000);
        });
    },
    async fetchProjectContracts () {
      this.loading = true;
      this.$store.dispatch('swiftuser/clearSavedContractDetails');
      await this.$store.dispatch('projects/fetchProjectContracts', {
        projectId: this.selectedProject.id,
      });
      this.loading = false;
    },
    async verifyContractDetails () {
      this.loading = true;
      await this.$store
        .dispatch('swiftuser/verifyContractDetails', {
          organizationExtId: this.selectedOrganization.organization.external_id,
          contractId: {
            contract_id: this.selectedContract,
          },
        })
        .then((response) => {
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.verificationFailed = true;
          this.verificationFailedMessage = error.response.data.message;
        });
    },
    async verifyBudgetDetails () {
      this.loading = true;
      await this.$store
        .dispatch('swiftuser/verifyWorkDetails', {
          organizationExtId: this.selectedOrganization.organization.external_id,
          details: {
            type: 'project-work',
            budget_id: this.selectedBudgetItem,
            issue_id: null,
          },
        })
        .then((response) => {
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.verificationFailed = true;
          this.verificationFailedMessage = error.response.data.message;
        });
    },
    async verifyIssueDetails () {
      this.loading = true;
      await this.$store
        .dispatch('swiftuser/verifyWorkDetails', {
          organizationExtId: this.selectedOrganization.organization.external_id,
          details: {
            type: 'project-issue',
            issue_id: this.selectedProjectIssue,
            budget_id: null,
          },
        })
        .then((response) => {
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.verificationFailed = true;
          this.verificationFailedMessage = error.response.data.message;
        });
    },
    async documentVerification () {
      this.$store
        .dispatch('swiftuser/verifyDocuments', {
          organizationExtId: this.selectedOrganization.organization.external_id,
        })
        .then((response) => {
          if (this.isWorkerDocVerifyFailed === true) {
            return;
          }
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.locationFetchFailed = true;
          this.verificationFailed = true;
          this.verificationFailedMessage = error.response.data.message;
        });
    },
    async checkIn () {
      if (this.loading) return;
      this.verificationFailedMessage = '';

      if (this.$refs.form.validate() === false) {
        this.$store.dispatch(
          'alert/onAlert',
          {
            message: 'Fill all required fields.',
            type: Constants.ALERT_TYPE_ERROR,
          },
          { root: true },
        );
        return;
      }
      if (!this.selectedProject) {
        this.$store.dispatch(
          'alert/onAlert',
          {
            message: 'Please select a project.',
            type: Constants.ALERT_TYPE_ERROR,
          },
          { root: true },
        );
        return;
      }
      if (this.selectedCheckinType === null) {
        this.$store.dispatch(
          'alert/onAlert',
          {
            message: 'Select a check-in type.',
            type: Constants.ALERT_TYPE_ERROR,
          },
          { root: true },
        );
        return;
      }
      if (
        this.verificationFailedMessage !== '' ||
        this.isWorkerDocVerifyFailed === true
      ) {
        return;
      }
      if (
        this.questionaireEnableCheck === true &&
        this.questions.length !== Object.keys(this.answerSelected).length
      ) {
        this.$store.dispatch(
          'alert/onAlert',
          {
            message: 'All questions are mandatory.',
            type: Constants.ALERT_TYPE_ERROR,
          },
          { root: true },
        );
        return;
      }
      (await this.locationCheck) && (await this.verifyLocation());
      if (this.selectedCheckinType === 'contractor') {
        await this.verifyContractDetails();
      }
      if (this.selectedCheckinType === 'worker') {
        if (
          this.isBudgetCheckin &&
          this.isIssueCheckin &&
          this.projectIssues.length === 0 &&
          this.projectBudgets.length === 0
        ) {
          return;
        }
        // await this.verifyWorkDetails();
        this.isBudgetCheckin &&
          !this.isIssueCheckin &&
          (await this.verifyBudgetDetails());

        this.isIssueCheckin &&
          !this.isBudgetCheckin &&
          (await this.verifyIssueDetails());

        if (this.isBudgetCheckin && this.isIssueCheckin) {
          if (this.workType === 'project-work') {
            this.isBudgetCheckin && (await this.verifyBudgetDetails());
          } else {
            this.isIssueCheckin && (await this.verifyIssueDetails());
          }
        }
      }
      // await this.fetchLocationDetails();
      if (this.verificationFailedMessage !== '') {
        return;
      }
      this.loading = true;
      this.$store
        .dispatch('swiftuser/checkInUser', {
          orgExtId: this.selectedOrganization.organization.external_id,
          details: {
            checkin_time: moment.utc().format(),
            location_id:
              typeof this.selectedGate === 'object'
                ? this.selectedGate.id
                : this.selectedGate,
            type: this.selectedCheckinType,
            checkin_method_type: 'manual-checkin',
            location_access: !!(
              this.longitudeValue != null && this.latitudeValue != null
            ),
          },
        })
        .then((response) => {
          this.loading = true;
          this.completeCheckInProcess();
        })
        .catch((error) => {
          this.verificationFailed = true;
          this.verificationFailedMessage = error.response.data.message;
          this.loading = false;
        });
    },
    async completeCheckInProcess () {
      const answerSelected = [];
      answerSelected.push({
        answers: this.answerSelected,
      });
      this.loading = true;
      if (this.isQuestionaire) {
        await this.$store
          .dispatch('questions/sendUserSelectedAnswers', {
            answers: answerSelected[0],
            checkInId: this.checkInId.id,
          })
          .then((response) => {})
          .catch(() => {
            this.loading = false;
          });
      }
      if (this.selectedCheckinType === 'visitor') {
        this.loading = true;
        this.$store
          .dispatch('swiftuser/sendVisitDetails', {
            purpose: {
              purpose_of_visit: this.visitPurpose,
            },
            checkInId: this.checkInId.id,
          })
          .then((response) => {
            this.loading = false;
            this.$store.dispatch('alert/onAlert', {
              message: 'Check-in completed successfully.',
              type: Constants.ALERT_TYPE_SUCCESS,
            });
            const checkInId = this.checkInId.id;
            localStorage.setItem('checkIn', checkInId);
            this.$router.push({ name: 'Checkin Success' });
          })
          .catch(() => {
            this.loading = false;
          });
      }
      if (this.selectedCheckinType === 'contractor') {
        this.loading = true;
        this.$store
          .dispatch('swiftuser/sendContractDetails', {
            details: {
              contract_id: this.selectedContract,
            },
            checkInId: this.checkInId.id,
          })
          .then((response) => {
            this.loading = false;
            this.$store.dispatch('alert/onAlert', {
              message: 'Check-in completed successfully.',
              type: Constants.ALERT_TYPE_SUCCESS,
            });
            const checkInId = this.checkInId.id;
            localStorage.setItem('checkIn', checkInId);
            this.$router.push({ name: 'Checkin Success' });
          })
          .catch(() => {
            this.loading = false;
          });
      }
      if (this.selectedCheckinType === 'staff') {
        this.$router.push({ name: 'Checkin Success' });
        this.loading = false;
      }
      if (this.selectedCheckinType === 'worker') {
        if (!this.isBudgetCheckin && !this.isIssueCheckin) {
          this.loading = true;
          this.$store.dispatch('alert/onAlert', {
            message: 'Check-in completed successfully.',
            type: Constants.ALERT_TYPE_SUCCESS,
          });
          const checkInId = this.checkInId.id;
          localStorage.setItem('checkIn', checkInId);
          this.$router.push({ name: 'Checkin Success' });
        }
        this.isBudgetCheckin &&
          !this.isIssueCheckin &&
          this.sendBudgetDetails();
        this.isIssueCheckin && !this.isBudgetCheckin && this.sendIssueDetails();

        if (this.isBudgetCheckin && this.isIssueCheckin) {
          if (this.workType === 'project-work') {
            this.isBudgetCheckin && this.sendBudgetDetails();
          } else {
            this.isIssueCheckin && this.sendIssueDetails();
          }
        }
      }
    },
    async sendBudgetDetails () {
      this.loading = true;
      await this.$store
        .dispatch('swiftuser/sendWorkDetails', {
          details: {
            budget_id: this.selectedBudgetItem,
            budget_instance_id: null,
            issue_id: null,
            type: 'project-work',
          },
          checkInId: this.checkInId.id,
        })
        .then((response) => {
          this.$store.dispatch('alert/onAlert', {
            message: 'Check-in completed successfully.',
            type: Constants.ALERT_TYPE_SUCCESS,
          });
          const checkInId = this.checkInId.id;
          localStorage.setItem('checkIn', checkInId);
          this.$router.push({ name: 'Checkin Success' });
          this.loading = false;
        })
        .catch((error) => {
          this.verificationFailed = true;
          this.verificationFailedMessage = error.response.data.message;
          this.loading = false;
        });
    },
    async sendIssueDetails () {
      this.loading = true;
      await this.$store
        .dispatch('swiftuser/sendWorkDetails', {
          details: {
            budget_id: null,
            budget_instance_id: null,
            issue_id: this.selectedProjectIssue,
            type: 'project-issue',
          },
          checkInId: this.checkInId.id,
        })
        .then((response) => {
          this.$store.dispatch('alert/onAlert', {
            message: 'Check-in completed successfully.',
            type: Constants.ALERT_TYPE_SUCCESS,
          });
          const checkInId = this.checkInId.id;
          localStorage.setItem('checkIn', checkInId);
          this.loading = false;
          this.$router.push({ name: 'Checkin Success' });
        })
        .catch((error) => {
          this.verificationFailed = true;
          this.verificationFailedMessage = error.response.data.message;
          this.loading = false;
        });
    },
  },
};
</script>
<style scoped>
.theme--dark.v-list {
  background-color: white !important;
}

.v-input--selection-controls__input .v-icon {
  color: #ccc !important;
}

.theme--dark >>> .v-list-item {
  color: #37474f !important;
}

.theme--dark.v-label {
  color: rgba(255, 255, 255, 0.7);
}

.theme--dark.v-messages {
  color: #37474f !important;
}

.hint-text {
  font-size: 13px;
  color: #444444;
  margin-top: -13px !important;
}

.class-disabled .v-input--selection-controls__input .v-icon {
  color: #000 !important;
}

.disabled-class {
  color: #ccc !important;
}

.checkin .mdi-checkbox-marked::before {
  color: #0000 !important;
  caret-color: #0000 !important;
}

.small-select {
  text-transform: capitalize !important;
}

::v-deep .v-input--selection-controls__input .v-icon {
  color: rgb(8, 8, 8) !important;
}

.v-list-item__title {
  color: #0000 !important;
}

.v-radio {
  display: flex;
  justify-content: center;
  align-items: center;
}

.v-input--selection-controls {
  margin: 0px !important;
  padding-top: 0px !important;
}

.type-bottom {
  padding: 0px !important;
  margin: 0px !important;
}

.content-section {
  height: 100vh;
}

.square-chip {
  width: 200px;
  height: 50px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  white-space: normal;
  font-size: 14px;
  border: 1px solid #66bb6a;
}

.selected-chip {
  background-color: #e0f2f1 !important;
  /* example light green */
  border-color: #66bb6a !important;
  color: #004d40 !important;
}
.location-wrapper {
  margin-top: 8px;
}
.v-autocomplete.v-select.v-input--is-focused >>> input {
  min-width: 0px !important;
}
.location-label-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.location-icon {
  color: #9e9e9e; /* dull icon */
}

.location-label {
  font-size: 12px;
  color: #9e9e9e; /* dull label */
  font-weight: 500;
}

.location-value {
  margin-left: 20px; /* aligns under text, not icon */
  font-size: 15px;
  font-weight: 600;
  color: #000000 !important; /* strong black */
}
.project-name {
  font-size: 14px;
  font-weight: 600;
  color: #263238 !important;
}

.project-location {
  font-size: 12px;
  color: #263238 !important;
  display: flex;
  align-items: center;
}

.selected-project {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.selected-project-name {
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: 600;
  color: #263238;
}
</style>
